import { useQuery } from '@tanstack/react-query';
import { Spin, Typography } from 'antd';
import { fetchAllForExport } from '../../api/leadsExport';
import { EyebrowLabel, space } from '@viq-admin/ui';

interface Props {
  oid: string;
}

/**
 * Aggregated activity stats for a user, derived from the leads-export
 * endpoint. "Recent captures" list is deferred for v2 — LeadExportRow
 * does not carry a lead id, so per-capture links cannot be built here.
 */
export function ActivitySummary({ oid }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['activitySummary', oid],
    queryFn: () => fetchAllForExport({ repOid: oid }),
    enabled: !!oid,
  });

  if (isLoading) return <Spin size="small" />;

  const rows = data?.rows ?? [];

  const captureCount = rows.length;
  const opportunityCount = rows.filter((r) => r.topOpportunityUsd !== '').length;
  const totalPipeline = rows.reduce((sum, r) => {
    const m = r.topOpportunityUsd.match(/\$([\d,]+)/);
    return sum + (m ? Number(m[1].replace(/,/g, '')) : 0);
  }, 0);

  const conferences = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.conferenceName] = (acc[r.conferenceName] ?? 0) + 1;
    return acc;
  }, {});
  const topConferenceEntry = Object.entries(conferences).sort((a, b) => b[1] - a[1])[0];

  return (
    <div>
      <EyebrowLabel>Activity Summary</EyebrowLabel>
      {captureCount === 0 ? (
        <Typography.Text type="secondary">No activity recorded.</Typography.Text>
      ) : (
        <>
          <Typography.Text>
            {captureCount} captures · {opportunityCount} opportunities · ${totalPipeline.toLocaleString()} total pipeline
          </Typography.Text>
          {topConferenceEntry && (
            <div style={{ marginTop: space.sm }}>
              <Typography.Text type="secondary">
                Most active conference: {topConferenceEntry[0]} ({topConferenceEntry[1]} captures)
              </Typography.Text>
            </div>
          )}
        </>
      )}
    </div>
  );
}
